import { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { Link, useLocation } from "@/lib/router-compat";
import { logNotFound } from "@/lib/siteMetrics.functions";
import { ArrowLeft, Zap, Sun, Flame, PlugZap, BatteryCharging, Building2, Sparkles, TrendingUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { buildTitle } from "@/config/seoTitles";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/landing/Footer";

const NotFound = () => {
  const location = useLocation();
  const title = buildTitle("", "notfound");

  useEffect(() => {
    if (typeof window === "undefined") return;
    // Loggar träffen så vi kan lägga in omdirigeringar för gamla URL:er.
    const args: { path: string; referrer?: string; userAgent?: string } = {
      path: location.pathname,
    };
    if (document.referrer) args.referrer = document.referrer;
    if (navigator.userAgent) args.userAgent = navigator.userAgent;
    void logNotFound({ data: args }).catch(() => {});
  }, [location.pathname]);


  useEffect(() => {
    document.title = title;
  }, [title]);

  return (
    <div className="min-h-dvh bg-background flex flex-col">
      <Helmet>
        <title>{title}</title>
        <meta name="robots" content="noindex, follow" />
        <meta name="description" content="Sidan du sökte kunde inte hittas på Elmarknad.se." />
        <meta property="og:title" content={title} />
        <meta property="og:description" content="Sidan du sökte kunde inte hittas på Elmarknad.se." />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary" />
      </Helmet>

      <Navbar />

      <main className="flex-1 flex items-center justify-center">
        <div className="page-container py-20 md:py-32">
          <div className="mx-auto max-w-xl text-center">
            <p className="font-mono text-sm uppercase tracking-[0.2em] text-muted-foreground">
              Felkod 404
            </p>
            <h1 className="mt-6 text-[6rem] font-bold leading-none tracking-tighter text-foreground sm:text-[8rem]">
              404
            </h1>
            <div className="mx-auto mt-8 h-px w-24 bg-gradient-to-r from-transparent via-primary/50 to-transparent" />
            <p className="mt-8 text-lg leading-relaxed text-muted-foreground md:text-xl">
              Sidan du letar efter finns inte. Den kan ha flyttats, tagits bort eller så är adressen felaktig.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-3">
              <Button asChild size="lg" className="w-full sm:w-auto">
                <Link to="/">
                  <ArrowLeft className="!size-4" />
                  Tillbaka till startsidan
                </Link>
              </Button>
            </div>

            <div className="mt-12">
              <p className="text-sm font-medium uppercase tracking-wider text-muted-foreground mb-4">
                Populära kategorier
              </p>
              <div className="flex flex-wrap justify-center gap-2">
                <Button asChild variant="outline" size="sm" className="gap-2">
                  <Link to="/elavtal/jamfor"><Zap className="!size-4" /> Jämför elavtal</Link>
                </Button>
                <Button asChild variant="outline" size="sm" className="gap-2">
                  <Link to="/elpriser"><TrendingUp className="!size-4" /> Elpriser</Link>
                </Button>
                <Button asChild variant="outline" size="sm" className="gap-2">
                  <Link to="/elbolag"><Building2 className="!size-4" /> Elbolag</Link>
                </Button>
                <Button asChild variant="outline" size="sm" className="gap-2">
                  <Link to="/solceller"><Sun className="!size-4" /> Solceller</Link>
                </Button>
                <Button asChild variant="outline" size="sm" className="gap-2">
                  <Link to="/varmepumpar"><Flame className="!size-4" /> Värmepumpar</Link>
                </Button>
                <Button asChild variant="outline" size="sm" className="gap-2">
                  <Link to="/laddboxar"><PlugZap className="!size-4" /> Laddboxar</Link>
                </Button>
                <Button asChild variant="outline" size="sm" className="gap-2">
                  <Link to="/batterier"><BatteryCharging className="!size-4" /> Hembatterier</Link>
                </Button>
                <Button asChild variant="outline" size="sm" className="gap-2">
                  <Link to="/ai"><Sparkles className="!size-4" /> Elmarknad AI</Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </main>


      <Footer />
    </div>
  );
};

export default NotFound;
